"use client";

import { useEffect, useState } from "react";
import CVADrawer from "components/modals/common/CVADrawer";
import CVAModal from "components/modals/common/CVAModal";
import useCurrency from "hooks/swr/useCurrency";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import { useBookingSummary } from "./useBookingSummary";
const RATE_MIN = 0;
const RATE_MAX = 50;
const RATE_STEP = 1;
const clampRate = (rate: number) => Math.min(RATE_MAX, Math.max(RATE_MIN, rate));
type Props = {
  isOpen: boolean;
  currentRate: number;
  onClose: () => void;
  onConfirm: (rate: number) => void;
};
const ModalBody = ({
  currentRate,
  onClose,
  onConfirm
}: Omit<Props, "isOpen">) => {
  const {
    formatter,
    rate: fxRate
  } = useCurrency();
  const {
    rent,
    taxesAndFees,
    taUpliftDiff,
    taUpliftMultiplier,
    taUpliftCondition
  } = useBookingSummary();
  const [draftRate, setDraftRate] = useState(clampRate(currentRate));
  useEffect(() => {
    setDraftRate(clampRate(currentRate));
  }, [currentRate]);
  const decrement = () => setDraftRate(r => Math.max(RATE_MIN, r - RATE_STEP));
  const increment = () => setDraftRate(r => Math.min(RATE_MAX, r + RATE_STEP));
  const displayedCommission = draftRate / 100 * rent;
  const upliftRate = draftRate > taUpliftCondition ? draftRate * taUpliftMultiplier : draftRate;
  const upliftedAmount = draftRate > 10 ? upliftRate / 100 * rent : displayedCommission;
  const displayedTotal = rent + (taxesAndFees - taUpliftDiff) + upliftedAmount;
  const canDecrement = draftRate > RATE_MIN;
  const canIncrement = draftRate < RATE_MAX;
  return <div className='flex w-full flex-col gap-6' data-sentry-component="ModalBody" data-sentry-source-file="CommissionRateModal.tsx">
      <div className='flex flex-col gap-2'>
        <p className='text-[18px] font-bold leading-[1.5] tracking-[-0.27px] text-textColor md:text-[18px]'>
          Change your commission rate
        </p>
        <p className='text-[14px] font-medium leading-[1.6] tracking-[-0.14px] text-Gray-700'>
          By default your commission is 10% of the booking cost before taxes and
          fees. Your commission is built into the total price and remains
          private when shared.
        </p>
      </div>

      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <p className='text-[14px] font-medium leading-[20px] text-Gray-700'>
            Commission rate
          </p>
          <div className='flex w-[120px] items-center justify-between'>
            <button type='button' onClick={decrement} disabled={!canDecrement} aria-label='Decrease commission rate' className={`flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-accent-500 bg-white text-[18px] font-bold text-accent-500 shadow-[0_2px_8px_0_rgba(34,34,34,0.08)] ${!canDecrement ? "opacity-50" : ""}`}>
              −
            </button>
            <p className='text-[16px] font-semibold leading-[19.5px] text-Gray-700'>
              {draftRate}%
            </p>
            <button type='button' onClick={increment} disabled={!canIncrement} aria-label='Increase commission rate' className={`flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-accent-500 bg-white text-[18px] font-bold text-accent-500 shadow-[0_2px_8px_0_rgba(34,34,34,0.08)] ${!canIncrement ? "opacity-50" : ""}`}>
              +
            </button>
          </div>
        </div>

        <div className='h-[1px] w-full bg-Gray-200' />

        <div className='flex flex-col gap-2'>
          <div className='flex items-center gap-2'>
            <p className='flex-1 text-[14px] font-medium leading-none tracking-[-0.14px] text-Gray-700'>
              Total commission
            </p>
            <p className='text-right text-[18px] leading-none tracking-[-0.18px] text-Emerald-600'>
              {formatter(2, 2).format(displayedCommission * fxRate)}
            </p>
          </div>
          <div className='flex items-center gap-2 font-bold leading-none text-brand-dark'>
            <p className='text-[14px] font-bold tracking-[-0.14px]'>
              Total stay
            </p>
            <p className='flex-1 text-right text-[18px] font-bold tracking-[-0.18px]'>
              {formatter(0, 0).format(displayedTotal * fxRate)}
            </p>
          </div>
        </div>
      </div>

      <div className='flex w-full flex-col gap-4'>
        <button type='button' onClick={onClose} className='flex h-11 w-full items-center justify-center rounded-[8px] border border-Gray-600 bg-white px-6 py-2 text-[16px] font-bold leading-[28px] text-Gray-600 shadow-[0_4px_16px_0_rgba(34,34,34,0.08)]'>
          Cancel
        </button>
        <button type='button' onClick={() => onConfirm(clampRate(draftRate))} className='flex h-11 w-full items-center justify-center rounded-[8px] bg-accent-500 px-6 py-2 text-[16px] font-bold leading-[28px] text-white shadow-[0_4px_16px_0_rgba(34,34,34,0.08)]'>
          Confirm rate
        </button>
      </div>
    </div>;
};
const CommissionRateModal = ({
  isOpen,
  currentRate,
  onClose,
  onConfirm
}: Props) => {
  const [isSmallerThan767] = useMediaQuery("(max-width: 767px)");
  if (!isOpen) return null;
  return !isSmallerThan767 ? <CVAModal isOpen={isOpen} onClose={onClose} intent='modal' size='large' className='relative w-[380px!important] rounded-[8px!important] px-10 pb-10 pt-12' classNameCloseIcon='text-Gray-500' data-sentry-element="CVAModal" data-sentry-component="CommissionRateModal" data-sentry-source-file="CommissionRateModal.tsx">
      <ModalBody currentRate={currentRate} onClose={onClose} onConfirm={onConfirm} data-sentry-element="ModalBody" data-sentry-source-file="CommissionRateModal.tsx" />
    </CVAModal> : <CVADrawer isOpen={isOpen} onClose={onClose} intent='bottom' size='small' className='rounded-t-[8px!important] px-8 pb-8 pt-12' classNameCloseIcon='text-Gray-500' classNameDialog='!z-[999]' data-sentry-element="CVADrawer" data-sentry-component="CommissionRateModal" data-sentry-source-file="CommissionRateModal.tsx">
      <ModalBody currentRate={currentRate} onClose={onClose} onConfirm={onConfirm} data-sentry-element="ModalBody" data-sentry-source-file="CommissionRateModal.tsx" />
    </CVADrawer>;
};
export default CommissionRateModal;